<template>
  <div class="container-fluid">
    <h1>Обращения LMS</h1>
    <p class="text-muted">Кто, когда и какой файл прислал через API интеграции и что мы ответили. Присланные файлы можно скачать.</p>

    <div class="row mb-3">
      <div class="col-md-2">
        <label class="form-label">LMS</label>
        <input class="form-control" v-model="params.lmsName" placeholder="INDIGO, Bilimal…">
      </div>
      <div class="col-md-2">
        <label class="form-label">Пользователь</label>
        <input class="form-control" v-model="params.username">
      </div>
      <div class="col-md-1">
        <label class="form-label">Месяц</label>
        <input type="number" min="1" max="12" class="form-control" v-model.number="params.accountingMonth">
      </div>
      <div class="col-md-1">
        <label class="form-label">Год</label>
        <input type="number" min="2024" class="form-control" v-model.number="params.accountingYear">
      </div>
      <div class="col-md-2">
        <label class="form-label">С</label>
        <input type="date" class="form-control" v-model="fromDate">
      </div>
      <div class="col-md-2">
        <label class="form-label">По</label>
        <input type="date" class="form-control" v-model="toDate">
      </div>
      <div class="col-md-2 d-flex align-items-end">
        <label><input type="checkbox" v-model="params.onlyErrors"> Только ошибки</label>
      </div>
    </div>
    <div class="mb-3">
      <button class="btn btn-primary" @click="fetch(true)" :disabled="loading">{{ loading ? "Загрузка..." : "Поиск" }}</button>
      <span class="ms-3">Найдено: {{ total }}</span>
    </div>

    <b-pagination v-model="currentPage" :total-rows="total" :per-page="params.pageSize"></b-pagination>

    <el-table :data="rows" style="width: 100%" border :row-class-name="rowClassName" row-key="entry.id" @expand-change="onExpand">
      <el-table-column type="expand">
        <template #default="props">
          <div class="details">
            <div><b>Запрос:</b> {{ props.row.entry.method }} {{ props.row.entry.path }}<span v-if="props.row.entry.queryString">?{{ props.row.entry.queryString }}</span></div>
            <div><b>IP:</b> {{ props.row.entry.ip }} · <b>request_id:</b> {{ props.row.entry.requestId }} · <b>время ответа:</b> {{ props.row.entry.durationMs }} мс</div>
            <div><b>Сохранён как:</b> {{ props.row.entry.storedPath || "—" }}</div>
            <div v-if="props.row.entry.bin || props.row.entry.lmsId"><b>Садик:</b> БИН {{ props.row.entry.bin || "—" }} · lms_id {{ props.row.entry.lmsId || "—" }} · регион {{ props.row.region || "—" }}</div>
            <div v-else-if="props.row.kindergartenCount > 0"><b>Садиков в файле:</b> {{ props.row.kindergartenCount }} · регион {{ props.row.region || (props.row.regionCount > 1 ? `несколько (${props.row.regionCount})` : "—") }}</div>
            <div><b>Наш ответ ({{ props.row.entry.status }}):</b></div>
            <pre class="response">{{ props.row.entry.responseBody }}</pre>
            <div v-if="results[props.row.entry.id]" class="mt-2">
              <b>Обработка из очереди по садикам:</b>
              <el-table :data="results[props.row.entry.id]" size="small" style="width: 100%">
                <el-table-column label="Когда" width="150"><template #default="s">{{ formatDate(s.row.result.occurredAt) }}</template></el-table-column>
                <el-table-column label="БИН" width="125"><template #default="s">{{ s.row.bin || "—" }}</template></el-table-column>
                <el-table-column label="lms_id" width="110"><template #default="s">{{ s.row.result.lmsId }}</template></el-table-column>
                <el-table-column label="Регион" width="150"><template #default="s">{{ s.row.region || "—" }}</template></el-table-column>
                <el-table-column label="Этап" width="110"><template #default="s">{{ s.row.result.stage === "UPLOAD" ? "Сохранение" : "Расчёт" }}</template></el-table-column>
                <el-table-column label="Итог" width="80"><template #default="s"><span :class="s.row.result.status === 'OK' ? 'text-success' : 'text-danger'">{{ s.row.result.status }}</span></template></el-table-column>
                <el-table-column label="Сообщение"><template #default="s">{{ s.row.result.message }}</template></el-table-column>
              </el-table>
              <div v-if="results[props.row.entry.id].length === 0" class="text-muted">Итогов из очереди пока нет.</div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="Когда" width="160">
        <template #default="scope">{{ formatDate(scope.row.entry.occurredAt) }}</template>
      </el-table-column>
      <el-table-column label="LMS" width="130"><template #default="scope">{{ scope.row.entry.lmsName || "—" }}</template></el-table-column>
      <el-table-column label="Пользователь" width="150"><template #default="scope">{{ scope.row.entry.username || "— (без токена)" }}</template></el-table-column>
      <el-table-column label="Что прислали" width="200">
        <template #default="scope">{{ endpointLabel(scope.row.entry.path) }}</template>
      </el-table-column>
      <el-table-column label="Период" width="80">
        <template #default="scope">
          <span v-if="scope.row.entry.accountingMonth">{{ scope.row.entry.accountingMonth }}/{{ scope.row.entry.accountingYear }}</span>
        </template>
      </el-table-column>
      <el-table-column label="Тип табеля" width="100">
        <template #default="scope">{{ timesheetTypeLabel(scope.row.entry.timesheetType) }}</template>
      </el-table-column>
      <el-table-column label="БИН" width="125">
        <template #default="scope">
          <span v-if="scope.row.entry.bin">{{ scope.row.entry.bin }}</span>
          <small v-else-if="scope.row.kindergartenCount > 0" class="text-muted">{{ kindergartensLabel(scope.row.kindergartenCount) }}</small>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="lms_id" width="110">
        <template #default="scope">
          <span v-if="scope.row.entry.lmsId">{{ scope.row.entry.lmsId }}</span>
          <small v-else-if="scope.row.kindergartenCount > 0" class="text-muted">см. строку ▸</small>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="Регион" width="150">
        <template #default="scope">
          <span v-if="scope.row.region">{{ scope.row.region }}</span>
          <small v-else-if="scope.row.regionCount > 1" class="text-muted">несколько ({{ scope.row.regionCount }})</small>
          <span v-else>—</span>
        </template>
      </el-table-column>
      <el-table-column label="Файл">
        <template #default="scope">
          <a v-if="scope.row.entry.storedPath" href="#" @click.prevent="download(scope.row.entry)">{{ scope.row.entry.fileName }}</a>
          <span v-else>{{ scope.row.entry.fileName || "—" }}</span>
          <small v-if="scope.row.entry.fileSize" class="text-muted"> ({{ formatSize(scope.row.entry.fileSize) }})</small>
        </template>
      </el-table-column>
      <el-table-column label="Ответ" width="220">
        <template #default="scope">
          <span :class="scope.row.entry.status >= 400 ? 'text-danger' : 'text-success'">{{ scope.row.entry.status }}</span>
          <small> {{ shorten(scope.row.entry.responseBody) }}</small>
        </template>
      </el-table-column>
      <el-table-column label="Обработка" width="200">
        <template #default="scope"><small v-html="processingLabel(scope.row)"></small></template>
      </el-table-column>
    </el-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";
import { ElMessage } from "element-plus";
import { downloadLmsFile, findLmsRequestLog, findLmsRequestResults, LmsRequestLogEntry, LmsRequestLogRow, LmsRequestLogSearchParams, LmsRequestResultRow } from "@/services/lmsLogService";
import { extractApiError } from "@/utils/apiError";

const TIMESHEET_TYPES: Record<number, string> = { 1: "план", 2: "факт" };

const ENDPOINTS: Record<string, string> = {
  "/api/v1/lms/upload/xlsx": "Табель (Excel)",
  "/api/v1/lms/upload-json/flat": "Табель (JSON)",
  "/api/v1/lms/upload-json/flat/zip": "Табель (JSON, zip)",
  "/api/v1/lms/upload-json/timesheet/general": "Общий табель (JSON)",
  "/api/v1/lms/upload-json/timesheet/general/zip": "Общий табель (JSON, zip)",
  "/api/v1/lms/attendance/info": "Посещаемость",
};

export default defineComponent({
  name: "LmsRequestLogView",
  setup() {
    const params = ref<LmsRequestLogSearchParams>({ page: 0, pageSize: 50, lmsName: null, username: null, accountingMonth: null, accountingYear: null, onlyErrors: false });
    const fromDate = ref<string | null>(null);
    const toDate = ref<string | null>(null);
    const rows = ref<LmsRequestLogRow[]>([]);
    const results = ref<Record<number, LmsRequestResultRow[]>>({});
    const total = ref(0);
    const loading = ref(false);
    const currentPage = ref(1);

    const fetch = async (resetPage = false) => {
      if (resetPage) currentPage.value = 1;
      params.value.page = currentPage.value - 1;
      params.value.from = fromDate.value ? new Date(fromDate.value).toISOString() : null;
      params.value.to = toDate.value ? new Date(new Date(toDate.value).getTime() + 86_400_000).toISOString() : null;
      if (!params.value.accountingMonth) params.value.accountingMonth = null;
      if (!params.value.accountingYear) params.value.accountingYear = null;
      loading.value = true;
      try {
        const page = await findLmsRequestLog(params.value);
        rows.value = page.content;
        results.value = {};
        total.value = page.totalElements;
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось загрузить журнал"), type: "error", showClose: true });
      } finally {
        loading.value = false;
      }
    };

    const download = async (entry: LmsRequestLogEntry) => {
      try {
        await downloadLmsFile(entry);
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Файл не найден"), type: "error", showClose: true });
      }
    };

    const formatDate = (iso: string) => new Date(iso).toLocaleString("ru-RU");
    const formatSize = (bytes: number) => (bytes > 1_048_576 ? `${(bytes / 1_048_576).toFixed(1)} МБ` : `${Math.round(bytes / 1024)} КБ`);
    const shorten = (text: string | null) => (text && text.length > 80 ? text.slice(0, 80) + "…" : text ?? "");
    const endpointLabel = (path: string) => ENDPOINTS[path] ?? path;
    const timesheetTypeLabel = (type: number | null) => (type == null ? "" : TIMESHEET_TYPES[type] ?? `тип ${type}`);
    const kindergartensLabel = (n: number) => {
      const mod10 = n % 10, mod100 = n % 100;
      const word = mod10 === 1 && mod100 !== 11 ? "садик" : mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20) ? "садика" : "садиков";
      return `${n} ${word}`;
    };
    const rowClassName = ({ row }: { row: LmsRequestLogRow }) => (row.entry.status >= 400 || row.uploadError > 0 || row.calculationError > 0 ? "error-row" : "");
    const processingLabel = (row: LmsRequestLogRow) => {
      if (row.expectedCount == null) return "";
      const errors = row.uploadError + row.calculationError;
      const parts = [`отправлено садиков: ${row.expectedCount}`, `сохранено: ${row.uploadOk}`, `рассчитано: ${row.calculationOk}`];
      if (errors > 0) parts.push(`<span class="text-danger">ошибок: ${errors}</span>`);
      return parts.join("<br>");
    };
    const onExpand = async (row: LmsRequestLogRow, expanded: LmsRequestLogRow[]) => {
      if (!expanded.includes(row) || results.value[row.entry.id]) return;
      try {
        results.value[row.entry.id] = await findLmsRequestResults(row.entry.id);
      } catch (error) {
        ElMessage({ message: extractApiError(error, "Не удалось загрузить итоги обработки"), type: "error", showClose: true });
      }
    };

    watch(currentPage, () => fetch(false));
    onMounted(() => fetch(true));

    return { params, fromDate, toDate, rows, results, total, loading, currentPage, fetch, download, formatDate, formatSize, shorten, endpointLabel, timesheetTypeLabel, kindergartensLabel, rowClassName, processingLabel, onExpand };
  },
});
</script>

<style scoped>
.details { font-size: 13px; padding: 4px 12px; }
.response { white-space: pre-wrap; font-size: 12px; margin: 4px 0 0; max-height: 240px; overflow: auto; }
</style>
<style>
.el-table .error-row { background-color: #fdecea; }
</style>
